// SynthesisOverrides — the per-video "frozen content" envelope written by
// wave-7's v1→v2 backfill into the `video.synthesis_overrides` column.
//
// Each field is independently optional. Re-renders read whatever's present
// and skip the corresponding synthesis step:
//   - frozenScript present       → phase-a-script skips the Claude call and
//                                   uses these role buckets verbatim
//   - frozenOfferContent present → the compiler applies these to info-card
//                                   slots (headline/items/price/cta/currency)
//                                   before query resolution, so the v1 offer
//                                   card replays exactly
//   - pinnedAssets present       → the compiler uses these asset IDs to
//                                   satisfy the asset-media / music slots
//                                   instead of letting the gate re-pick
//   - themeOverrides present     → merged into the per-video override layer
//                                   of the resolution cascade (engine →
//                                   brand_kit → theseOverrides)
//
// The full design lives in docs/plans/video-template-engine-remaining.md
// (wave 7 brief).
import { z } from 'zod';
import { themeOverridesSchema } from './theme.js';
export const synthesisOverridesFrozenScriptSchema = z
    .object({
    hook: z.string().optional(),
    body: z.array(z.string()).optional(),
    cta: z.string().optional(),
    disclaimer: z.string().optional(),
    lists: z.array(z.array(z.string())).optional(),
})
    .strict();
/**
 * The declared-content sibling of `frozenScript`.
 *
 * `frozenScript` is a strict FOUR-ROLE object, so a video replaying through a
 * template that has migrated to a declared `content` schema would find none of
 * its copy and silently regenerate. Values are per-template and validated
 * against the doc's own declaration at synthesis time, so the envelope carries
 * them opaquely — exactly as `draftConfig.scriptContent` does.
 */
export const synthesisOverridesFrozenContentSchema = z.record(z.string().min(1), z.unknown());
export const synthesisOverridesFrozenOfferContentSchema = z
    .object({
    headline: z.string().optional(),
    items: z.array(z.string()).optional(),
    /** Decimal-formatted price string, e.g. '155.00'. */
    price: z.string().optional(),
    /** Currency code or symbol — preserves v1 raw value for lossless replay. */
    currency: z.string().optional(),
    cta: z.string().optional(),
})
    .strict();
export const synthesisOverridesPinnedAssetsSchema = z
    .object({
    beforeAssetId: z.string().optional(),
    afterAssetId: z.string().optional(),
    musicTrackId: z.string().optional(),
    /** TTS voice the user picked on the original v1 video. */
    voice: z.string().optional(),
})
    .strict();
// Runtime validator. Used by phase-a inputs + the wave-7 backfill writer.
export const synthesisOverridesSchema = z
    .object({
    themeOverrides: themeOverridesSchema.optional(),
    frozenScript: synthesisOverridesFrozenScriptSchema.optional(),
    frozenContent: synthesisOverridesFrozenContentSchema.optional(),
    frozenOfferContent: synthesisOverridesFrozenOfferContentSchema.optional(),
    pinnedAssets: synthesisOverridesPinnedAssetsSchema.optional(),
})
    .strict();
//# sourceMappingURL=synthesis-overrides.js.map